<script setup lang="ts">
const currentYear = new Date().getFullYear()

// Fetch navigation categories from database
const { data: categories } = useNavigationCategories()

// Group categories: Premium brands vs Main categories
const premiumBrands = computed(() =>
  categories.value?.filter(c => c.is_premium_brand) || []
)
const mainCategories = computed(() =>
  categories.value?.filter(c => !c.is_premium_brand) || []
)
</script>

<template>
  <footer class="bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 border-t-2 border-gray-200 dark:border-gray-800">
    <UContainer>
      <div class="py-12 md:py-16">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          <!-- Company Info -->
          <div>
            <div class="mb-6">
              <img
                src="/logo-acoxpa.png"
                alt="Pinturas Acoxpa - Granados"
                class="h-16 w-auto"
                width="180"
                height="64"
                loading="lazy"
              />
            </div>
            <p class="text-sm leading-relaxed text-gray-600 dark:text-gray-400 font-medium">
              Proveedor especializado en soluciones de pintura industrial para flotillas, construcción e industria en México.
            </p>

            <!-- Trust badge -->
            <div class="mt-6 inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 rounded-lg border-2 border-primary-100 dark:border-primary-900/50 shadow-sm">
              <UIcon name="i-heroicons-shield-check-solid" class="w-5 h-5 text-primary-500" />
              <span class="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Distribuidor Autorizado de Pinturas Axalta</span>
            </div>
          </div>

          <!-- Categories -->
          <div>
            <h2 class="font-bold text-base text-gray-900 dark:text-white mb-5 uppercase tracking-wide">Categorías</h2>
            <ul class="space-y-3">
              <li>
                <NuxtLink
                  to="/catalogo"
                  class="group inline-flex items-center gap-2 text-base font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-all duration-200"
                >
                  <UIcon name="i-heroicons-chevron-right" class="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  Todos los Productos
                </NuxtLink>
              </li>
              <li v-for="cat in mainCategories" :key="cat.slug">
                <NuxtLink
                  :to="`/${cat.slug}`"
                  class="group inline-flex items-center gap-2 text-base font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-all duration-200"
                >
                  <div
                    class="w-2 h-2 rounded-full transition-transform group-hover:scale-125"
                    :style="{ backgroundColor: cat.brand_color }"
                  />
                  {{ cat.name }}
                </NuxtLink>
              </li>
            </ul>

            <!-- Premium Brands -->
            <h3 v-if="premiumBrands.length" class="font-bold text-sm text-gray-700 dark:text-gray-300 mt-6 mb-3 uppercase tracking-wide">
              Marcas Premium
            </h3>
            <ul class="space-y-3">
              <li v-for="brand in premiumBrands" :key="brand.slug">
                <NuxtLink
                  :to="`/${brand.slug}`"
                  class="group inline-flex items-center gap-2 text-base font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-all duration-200"
                >
                  <div
                    class="w-2 h-2 rounded-full transition-transform group-hover:scale-125"
                    :style="{ backgroundColor: brand.brand_color }"
                  />
                  {{ brand.name }}
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Quick Links -->
          <div>
            <h2 class="font-bold text-base text-gray-900 dark:text-white mb-5 uppercase tracking-wide">Enlaces Rápidos</h2>
            <ul class="space-y-3">
              <li>
                <NuxtLink
                  to="/cotizacion"
                  class="group inline-flex items-center gap-2 text-base font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-all duration-200"
                >
                  <UIcon name="i-heroicons-chevron-right" class="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  Mi Cotización
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  to="/contacto"
                  class="group inline-flex items-center gap-2 text-base font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-all duration-200"
                >
                  <UIcon name="i-heroicons-chevron-right" class="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  Contacto
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Contact -->
          <div>
            <h2 class="font-bold text-base text-gray-900 dark:text-white mb-5 uppercase tracking-wide">Contáctanos</h2>
            <ul class="space-y-4">
              <li>
                <a
                  href="mailto:pinturasacoxpa@gmail.com"
                  class="group flex items-center gap-3 p-3 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-all duration-200"
                >
                  <div class="flex-shrink-0 w-10 h-10 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                    <UIcon name="i-heroicons-envelope-solid" class="w-5 h-5 text-primary-500" />
                  </div>
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-primary-500 dark:group-hover:text-primary-400">
                    pinturasacoxpa@gmail.com
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+525582960319"
                  class="group flex items-center gap-3 p-3 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-all duration-200"
                >
                  <div class="flex-shrink-0 w-10 h-10 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center justify-center group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                    <UIcon name="i-heroicons-phone-solid" class="w-5 h-5 text-primary-500" />
                  </div>
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300 group-hover:text-primary-500 dark:group-hover:text-primary-400">
                    +52 55 8296 0319
                  </span>
                </a>
              </li>
              <li>
                <div class="flex items-start gap-3 p-3">
                  <div class="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <UIcon name="i-heroicons-map-pin-solid" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <span class="text-sm font-medium text-gray-600 dark:text-gray-400 pt-2">
                    Ciudad de México, México
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Copyright -->
      <div class="py-6 border-t-2 border-gray-200 dark:border-gray-800">
        <p class="text-center text-sm font-semibold text-gray-500 dark:text-gray-400">
          © {{ currentYear }} Granados Pinturas Industriales. Todos los derechos reservados.
        </p>
      </div>
    </UContainer>
  </footer>
</template>
